import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  certificateApi,
  certificateDownloadUrl,
} from '../../services/certificateApi';
import './CertificateValidationPage.css';

const CERTIFICATE_STATES = {
  vigente: {
    title: 'Constancia vigente',
    description: 'Esta constancia es auténtica y se encuentra vigente.',
    modifier: 'current',
  },
  reemplazada: {
    title: 'Constancia reemplazada',
    description:
      'Existe una versión posterior de esta constancia. Este documento ya no es la versión vigente.',
    modifier: 'replaced',
  },
  revocada: {
    title: 'Constancia revocada',
    description: 'Esta constancia ya no tiene validez.',
    modifier: 'revoked',
  },
};

const initialView = { status: 'loading', certificate: null };

function CertificateMessage({ title, description, modifier }) {
  return (
    <section
      className={`certificate-validation__message certificate-validation__message--${modifier}`}
      aria-live="polite"
    >
      <span className="certificate-validation__mark" aria-hidden="true">
        {modifier === 'current' ? '✓' : '!'}
      </span>
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </section>
  );
}

function CertificateDetails({ certificate }) {
  return (
    <dl className="certificate-validation__details">
      <div>
        <dt>Participante</dt>
        <dd>{certificate.nombre_alumno}</dd>
      </div>
      {certificate.nombre_clase && (
        <div>
          <dt>Clase</dt>
          <dd>{certificate.nombre_clase}</dd>
        </div>
      )}
      <div>
        <dt>Capacitación</dt>
        <dd>{certificate.capacitacion}</dd>
      </div>
      {certificate.fecha_capacitacion && (
        <div>
          <dt>Periodo</dt>
          <dd>{certificate.fecha_capacitacion}</dd>
        </div>
      )}
      <div>
        <dt>Duración</dt>
        <dd>{certificate.horas} horas</dd>
      </div>
      {certificate.calificacion !== null &&
        certificate.calificacion !== undefined && (
          <div>
            <dt>Calificación</dt>
            <dd>{certificate.calificacion}</dd>
          </div>
        )}
      <div>
        <dt>Fecha de expedición</dt>
        <dd>{certificate.fecha_expedicion}</dd>
      </div>
      <div>
        <dt>Documento</dt>
        <dd>Versión {certificate.version}</dd>
      </div>
    </dl>
  );
}

function CertificateDocument({ certificate }) {
  if (!certificate.pdf_url) return null;

  const viewUrl = certificateDownloadUrl(certificate.pdf_url);
  const downloadUrl = `${viewUrl}${viewUrl.includes('?') ? '&' : '?'}download=1`;

  return (
    <section
      className="certificate-validation__document"
      aria-labelledby="certificate-document-title"
    >
      <div className="certificate-validation__document-header">
        <div>
          <h2 id="certificate-document-title">Documento emitido</h2>
          <p>Consulta la constancia asociada con este código QR.</p>
        </div>
        <div className="certificate-validation__document-actions">
          <a href={viewUrl} target="_blank" rel="noreferrer">
            Abrir PDF
          </a>
          <a href={downloadUrl}>Descargar PDF</a>
        </div>
      </div>
      <iframe
        className="certificate-validation__document-frame"
        src={viewUrl}
        title="Vista previa de la constancia validada"
      />
    </section>
  );
}

function CertificateValidationPage() {
  const { token } = useParams();
  const [view, setView] = useState(initialView);

  useEffect(() => {
    let active = true;
    setView(initialView);

    certificateApi
      .validate(token)
      .then((certificate) => {
        if (!active) return;

        if (!CERTIFICATE_STATES[certificate?.estado]) {
          setView({ status: 'error', certificate: null });
          return;
        }

        setView({ status: 'success', certificate });
      })
      .catch((error) => {
        if (!active) return;
        const notFound =
          error?.status === 404 || error?.code === 'CERTIFICATE_NOT_FOUND';
        setView({
          status: notFound ? 'not-found' : 'error',
          certificate: null,
        });
      });

    return () => {
      active = false;
    };
  }, [token]);

  return (
    <main className="certificate-validation">
      <div className="certificate-validation__card">
        <header className="certificate-validation__header">
          <p className="certificate-validation__eyebrow">
            Grupo Derecho y Progreso
          </p>
          <p>Verificación pública de constancias</p>
        </header>

        {view.status === 'loading' && (
          <div className="certificate-validation__loading" role="status">
            <span className="certificate-validation__spinner" aria-hidden="true" />
            <p>Validando constancia…</p>
          </div>
        )}

        {view.status === 'not-found' && (
          <CertificateMessage
            title="Constancia no encontrada"
            description="No encontramos una constancia asociada con este enlace. Verifica que sea el enlace completo."
            modifier="not-found"
          />
        )}

        {view.status === 'error' && (
          <CertificateMessage
            title="No fue posible validar la constancia"
            description="Intenta nuevamente más tarde."
            modifier="error"
          />
        )}

        {view.status === 'success' && (
          <>
            <CertificateMessage {...CERTIFICATE_STATES[view.certificate.estado]} />
            <CertificateDetails certificate={view.certificate} />
            <CertificateDocument certificate={view.certificate} />
          </>
        )}

        <footer className="certificate-validation__footer">
          La información mostrada se limita a los datos necesarios para validar el documento.
        </footer>
      </div>
    </main>
  );
}

export default CertificateValidationPage;
